const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8081/api';

let portalUnauthorizedHandler: (() => void) | null = null;

export const setPortalUnauthorizedHandler = (handler: (() => void) | null): void => {
  portalUnauthorizedHandler = handler;
};

interface PortalRequestOptions {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: object | FormData;
  params?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  timeoutMs?: number;
  /** Cancela a requisição por fora (ex.: quem pediu saiu da tela). Compõe com `timeoutMs`. */
  signal?: AbortSignal;
}

// Compor, não escolher: com `signal ?? timeout`, quem passasse os dois perdia o teto de tempo em
// silêncio e a requisição do portal podia ficar pendurada (review do web#1190).
const sinalDaRequisicao = (
  signal: AbortSignal | undefined,
  timeoutMs: number | undefined,
): AbortSignal | undefined => {
  const sinais = [signal, timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined].filter(
    (sinal): sinal is AbortSignal => sinal !== undefined,
  );
  return sinais.length > 0 ? AbortSignal.any(sinais) : undefined;
};

let refreshInFlight: Promise<boolean> | null = null;

const refreshPortalSessionSingleFlight = (): Promise<boolean> => {
  if (!refreshInFlight) {
    const baseUrl = BASE_URL.replace(/\/$/, '');
    refreshInFlight = fetch(`${baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      credentials: 'include',
      signal: AbortSignal.timeout(10000),
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const portalRequest = async ({
  endpoint,
  method = 'GET',
  body,
  params,
  headers: customHeaders = {},
  skipAuth = false,
  timeoutMs,
  signal,
}: PortalRequestOptions): Promise<Response> => {
  const headers: Record<string, string> = {
    ...customHeaders,
  };

  let finalBody: BodyInit | null = null;
  if (body) {
    if (body instanceof FormData) {
      finalBody = body;
    } else {
      headers['Content-Type'] = 'application/json';
      finalBody = JSON.stringify(body);
    }
  }

  const baseUrl = BASE_URL.replace(/\/$/, '');
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = new URL(
    `${baseUrl}${cleanEndpoint}`,
    typeof window !== 'undefined' ? window.location.origin : undefined,
  );

  if (params) {
    Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, String(value)));
  }

  const doFetch = (): Promise<Response> =>
    fetch(url.toString(), {
      method,
      headers,
      body: method !== 'GET' ? finalBody : undefined,
      credentials: 'include',
      signal: sinalDaRequisicao(signal, timeoutMs),
    });

  let response = await doFetch();

  if (response.status === 401 && !skipAuth) {
    const refreshed = await refreshPortalSessionSingleFlight();
    if (refreshed) {
      response = await doFetch();
    }
    if (response.status === 401) {
      portalUnauthorizedHandler?.();
      throw new Error('Sessão expirada');
    }
  }

  return response;
};

export const portalGet = (
  endpoint: string,
  params?: Record<string, string | number | boolean>,
  skipAuth = false,
  headers?: Record<string, string>,
  timeoutMs?: number,
) => portalRequest({ endpoint, method: 'GET', params, skipAuth, headers, timeoutMs });

export const portalPost = (
  endpoint: string,
  body?: object | FormData,
  skipAuth = false,
  headers?: Record<string, string>,
  signal?: AbortSignal,
) => portalRequest({ endpoint, method: 'POST', body, skipAuth, headers, signal });

export const portalPatch = (
  endpoint: string,
  body?: object | FormData,
  skipAuth = false,
  headers?: Record<string, string>,
) => portalRequest({ endpoint, method: 'PATCH', body, skipAuth, headers });
